import { useOrganization, useOrganizationList } from "@clerk/tanstack-react-start"
import { api } from "@stoneflow/backend/convex/_generated/api"
import type { Doc, Id } from "@stoneflow/backend/convex/_generated/dataModel"
import { Outlet, createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useEffect, useRef } from "react"
import { AppHeader } from "#/components/app-header"
import { BillingBanner } from "#/components/billing/billing-banner"
import { DemoBanner } from "#/components/demo-banner"
import { RouteError } from "#/components/route-error"
import { Spinner } from "#/components/ui/spinner"
import { useAppNavigate } from "#/hooks/use-app-navigate"
import { useRoles } from "#/hooks/use-roles"
import { useIsTenantAdmin } from "#/hooks/useIsTenantAdmin"
import { useOrgInit } from "#/hooks/useOrgInit"
import { setDemoMode, trackEvent } from "#/lib/posthog"
import { StonelogContext } from "#/lib/stonelog-context"

export const Route = createFileRoute("/_authenticated/stonelog")({
	component: StonelogLayout,
	errorComponent: RouteError,
})

const NO_LOG_CONTEXT = { currentLogId: null as Id<"logs"> | null, currentLog: null }

function LogProvider({
	logId,
	children,
	canAdmin,
	orgName,
	demoExpiresAt,
	logs,
	onSwitchLog,
	tenants,
	currentTenantId,
	onSwitchTenant,
}: {
	logId: Id<"logs">
	children: React.ReactNode
	canAdmin: boolean
	orgName: string | undefined
	demoExpiresAt: number | null
	logs: Doc<"logs">[] | undefined
	onSwitchLog: (logId: Id<"logs">) => void
	tenants: { id: string; name: string }[]
	currentTenantId: string | undefined
	onSwitchTenant: (orgId: string) => void
}) {
	const currentLog = useQuery(api.stonelog.logs.get, { logId })
	const isDemo = demoExpiresAt !== null

	const contextValue = {
		currentLogId: logId,
		currentLog: (currentLog ?? null) as Doc<"logs"> | null,
	}

	return (
		<StonelogContext.Provider value={contextValue}>
			<div className="flex min-h-screen flex-col bg-background">
				<AppHeader
					currentLog={currentLog ?? null}
					logs={logs ?? []}
					onSwitchLog={onSwitchLog}
					canAdmin={canAdmin}
					orgName={orgName}
					isDemo={isDemo}
					tenants={tenants}
					currentTenantId={currentTenantId}
					onSwitchTenant={onSwitchTenant}
				/>
				{isDemo && <DemoBanner expiresAt={demoExpiresAt} />}
				<BillingBanner />
				<main className="flex-1">{children}</main>
			</div>
		</StonelogContext.Provider>
	)
}

function StonelogLayout() {
	const { isReady } = useOrgInit()
	// Sticky-ready: once the layout has rendered children, stay rendering them even if
	// `isReady` briefly flips back to false during a tenant switch (Convex refetches
	// orgSettings under the new JWT). This avoids a full-page spinner on tenant change —
	// child queries gated on `isReady` will skip and components render their own
	// section-level loading states.
	const hasEverBeenReady = useRef(false)
	if (isReady) hasEverBeenReady.current = true

	const { organization } = useOrganization()
	const {
		userMemberships,
		setActive,
		isLoaded: isOrgListLoaded,
	} = useOrganizationList({
		userMemberships: { infinite: true, pageSize: 100 },
	})
	const navigate = useAppNavigate()

	const tenants = isOrgListLoaded
		? (userMemberships.data ?? []).map((m) => ({
				id: m.organization.id,
				name: m.organization.name,
			}))
		: []
	const handleSwitchTenant = (orgId: string) => {
		if (!setActive) return
		setActive({ organization: orgId }).catch(console.error)
		trackEvent("tenant_switched", { org_id: orgId })
	}

	const prefs = useQuery(api.platform.userPreferences.get, isReady ? {} : "skip")
	const entitlement = useQuery(
		api.platform.entitlements.check,
		isReady ? { moduleSlug: "stonelog" } : "skip",
	)
	const userLogs = useQuery(api.stonelog.logs.listMyLogs, isReady ? {} : "skip")
	const switchLog = useMutation(api.stonelog.logs.switchLog)
	// In v2 permissions, "tenant admin" UI is gated by Clerk org:admin (or platform operator).
	const { isAdmin: canAdmin } = useIsTenantAdmin()
	const { isPlatformAdmin } = useRoles()

	const activeLogId = prefs?.activeLogId ?? null

	const handleSwitchLog = (logId: Id<"logs">) => {
		switchLog({ logId })
		trackEvent("stonelog_log_switched", { log_id: logId })
	}

	// Redirect to select-log if prefs loaded but no log selected,
	// OR if activeLogId points to a log the user no longer has access to
	// (e.g., they were unassigned since their last session).
	const activeLogStillAccessible =
		activeLogId && userLogs ? userLogs.some((l) => l._id === activeLogId) : true
	useEffect(() => {
		if (!isReady || prefs === undefined) return
		if (!activeLogId) {
			navigate({ to: "/stonelog/select-log" })
			return
		}
		if (userLogs && !activeLogStillAccessible) {
			navigate({ to: "/stonelog/select-log" })
		}
	}, [isReady, prefs, activeLogId, userLogs, activeLogStillAccessible, navigate])

	const demoSession = useQuery(
		api.demo.session.getByOrg,
		isReady && organization?.id ? { orgId: organization.id } : "skip",
	)
	const demoExpiresAt =
		demoSession && demoSession.status === "active" ? demoSession.expiresAt : null

	const orgName = organization?.name ?? undefined
	const isDemo = demoExpiresAt !== null

	useEffect(() => {
		setDemoMode(isDemo)
	}, [isDemo])

	if (!isReady && !hasEverBeenReady.current) {
		return (
			<div className="flex items-center justify-center p-12">
				<Spinner />
			</div>
		)
	}

	// In v2 permissions, tenant disable lives on orgSettings.status (enforced server-side by
	// requireAuth). The frontend gate here only checks for expired trials.
	const moduleDisabled =
		entitlement !== undefined &&
		(entitlement === null ||
			(entitlement.trialExpiresAt && entitlement.trialExpiresAt < Date.now()))

	if (moduleDisabled && !isPlatformAdmin) {
		const reason =
			entitlement?.trialExpiresAt && entitlement.trialExpiresAt < Date.now()
				? "Your Stonelog trial has expired."
				: "Stonelog is not currently available for your organization."
		return (
			<div className="flex min-h-screen flex-col bg-background">
				<AppHeader orgName={orgName} isDemo={isDemo} />
				{isDemo && demoExpiresAt && <DemoBanner expiresAt={demoExpiresAt} />}
				<BillingBanner />
				<div className="flex flex-1 items-center justify-center p-12">
					<div className="max-w-md space-y-3 text-center">
						<h2 className="text-lg font-medium">Module Unavailable</h2>
						<p className="text-sm text-foreground-secondary">
							{reason} Please contact your administrator.
						</p>
					</div>
				</div>
			</div>
		)
	}

	if (!activeLogId || !activeLogStillAccessible) {
		return (
			<StonelogContext.Provider value={NO_LOG_CONTEXT}>
				<div className="flex min-h-screen flex-col bg-background">
					<AppHeader
						canAdmin={canAdmin}
						orgName={orgName}
						isDemo={isDemo}
						tenants={tenants}
						currentTenantId={organization?.id}
						onSwitchTenant={handleSwitchTenant}
					/>
					{isDemo && demoExpiresAt && <DemoBanner expiresAt={demoExpiresAt} />}
					<BillingBanner />
					<main className="flex-1">
						<Outlet />
					</main>
				</div>
			</StonelogContext.Provider>
		)
	}

	// Wait for userLogs to confirm the activeLogId is still accessible before
	// mounting LogProvider — otherwise a stale activeLogId would trigger a
	// server-side access error on `logs.get` before our reactive redirect fires.
	if (userLogs === undefined) {
		return (
			<div className="flex items-center justify-center p-12">
				<Spinner />
			</div>
		)
	}

	return (
		<LogProvider
			logId={activeLogId}
			canAdmin={canAdmin}
			orgName={orgName}
			demoExpiresAt={demoExpiresAt}
			logs={userLogs}
			onSwitchLog={handleSwitchLog}
			tenants={tenants}
			currentTenantId={organization?.id}
			onSwitchTenant={handleSwitchTenant}
		>
			<Outlet />
		</LogProvider>
	)
}
